import React, { ChangeEvent, useRef } from 'react';
import * as XLSX from 'xlsx';
import { t } from '@lingui/core/macro';
import { DEFAULT_COLUMNS, LoaderOnFileUpload, extractDataFromRows } from './shared';
import { auth } from '../firebase';
import { TableMatrix } from '../utils/excelTable';
import { saveToGoogleSheets } from '../components/ExcelExporter';
import { useProject } from '../contexts/ProjectContext';

interface ExcelUploaderProps {
  onFileUpload: LoaderOnFileUpload;
}

const ExcelUploader: React.FC<ExcelUploaderProps> = ({ onFileUpload }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { selectedProject } = useProject();

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (!Array.isArray(jsonData) || jsonData.length === 0) {
          throw new Error('Invalid Excel file format');
        }

        const { allSheetDataRows, blocks, videoSettings, columns } = extractDataFromRows(jsonData as unknown[]);

        onFileUpload(
          allSheetDataRows,
          columns.length ? columns : DEFAULT_COLUMNS,
          firstSheetName,
          blocks.length > 0 ? blocks : undefined,
          videoSettings
        );

        // Also save the uploaded Excel to Google Sheets (if user is logged in)
        try {
          const userEmail: string | null = auth.currentUser?.email ?? null;
          if (userEmail && selectedProject?.id) {
            await saveToGoogleSheets(jsonData as TableMatrix, firstSheetName || 'Sheet1', userEmail, selectedProject.id);
          } else {
            console.log('[GSHEETS][Upload] No user email or project selected, skipping Google Sheets save');
          }
        } catch (gsErr) {
          console.error('[GSHEETS][Upload] Error saving uploaded Excel to Google Sheets:', gsErr);
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error reading Excel file:', error);
        alert('Failed to read Excel file. Please make sure it is a valid .xlsx file.');
      }
    };

    reader.onerror = () => {
      alert('Failed to read the file. Please try again.');
    };

    reader.readAsBinaryString(file);
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="excel-uploader">
      <button 
        className="upload-button"
        onClick={triggerFileInput}
      >
        <svg
          className="upload-icon"
          style={{ marginRight: '8px' }}
          fill="#fff"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 50 50"
        >
          <path
            d="M28.8125 .03125L.8125 5.34375C.339844 
          5.433594 0 5.863281 0 6.34375L0 43.65625C0 
          44.136719 .339844 44.566406 .8125 44.65625L28.8125 
          49.96875C28.875 49.980469 28.9375 50 29 50C29.230469 
          50 29.445313 49.929688 29.625 49.78125C29.855469 49.589844 
          30 49.296875 30 49L30 1C30 .703125 29.855469 .410156 29.625 
          .21875C29.394531 .0273438 29.105469 -.0234375 28.8125 .03125ZM32 
          6L32 13L34 13L34 15L32 15L32 20L34 20L34 22L32 22L32 27L34 27L34 
          29L32 29L32 35L34 35L34 37L32 37L32 44L47 44C48.101563 44 49 
          43.101563 49 42L49 8C49 6.898438 48.101563 6 47 6ZM36 13L44 
          13L44 15L36 15ZM6.6875 15.6875L11.8125 15.6875L14.5 21.28125C14.710938 
          21.722656 14.898438 22.265625 15.0625 22.875L15.09375 22.875C15.199219 
          22.511719 15.402344 21.941406 15.6875 21.21875L18.65625 15.6875L23.34375 
          15.6875L17.75 24.9375L23.5 34.375L18.53125 34.375L15.28125 
          28.28125C15.160156 28.054688 15.035156 27.636719 14.90625 
          27.03125L14.875 27.03125C14.8125 27.316406 14.664063 27.761719 
          14.4375 28.34375L11.1875 34.375L6.1875 34.375L12.15625 25.03125ZM36 
          20L44 20L44 22L36 22ZM36 27L44 27L44 29L36 29ZM36 35L44 35L44 37L36 37Z"
          ></path>
        </svg>
        {t`Upload Excel File`}
        <input
          ref={fileInputRef}
          className="file"
          name="file"
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </button>
    </div>
    );
};

export default ExcelUploader;


